import { forwardRef } from 'react'
import { t } from '@/i18n/translations'

interface AvatarVideoProps {
  avatarEnabled: boolean
  isConnecting: boolean
  // LemonSlice publishes a composition intended to be shown in full. Other
  // callers retain the existing cinematic crop by default.
  fitMode?: 'cover' | 'contain'
  // True once BEY's video track is attached to the <video> element. When
  // avatarEnabled && !avatarReady && !isConnecting, we show a translucent
  // "Preparing interviewer..." overlay on top of the (still empty) video.
  // Optional so legacy callers still work.
  avatarReady?: boolean
  // True when the worker told us BEY is at capacity (issue #22 Phase 1).
  // Replaces the preparing overlay with an "interviewer unavailable" message
  // and a refresh CTA. Optional so legacy callers still work.
  avatarUnavailable?: boolean
  // True when BEY refused after our retry budget (reason='bey_429_persistent').
  // The worker is alive in audio-only fallback — the candidate can continue
  // without the avatar. Distinct from avatarUnavailable (which is a dead-end).
  avatarDegraded?: boolean
  // Called when the candidate clicks "Continue without avatar" on the
  // degraded overlay. Caller should clear avatarDegraded so the overlay
  // dismisses and the Start button becomes available.
  onContinueDegraded?: () => void
  // Queue state (issue #22 Phase 2). When non-null, replaces the preparing
  // overlay with a "you're in line" message showing position + ETA.
  queuePosition?: number | null
  queueEtaSeconds?: number | null
  queueDepth?: number | null
  language?: string
}

function formatEtaText(seconds: number, language: string): string {
  if (seconds < 60) {
    return language === 'de' ? `weniger als 1 Minute` : `less than 1 minute`
  }
  const minutes = Math.round(seconds / 60)
  if (language === 'de') {
    return minutes === 1 ? `~1 Minute` : `~${minutes} Minuten`
  }
  return minutes === 1 ? `~1 minute` : `~${minutes} minutes`
}

export const AvatarVideo = forwardRef<HTMLVideoElement, AvatarVideoProps>(
  ({
    avatarEnabled,
    isConnecting,
    fitMode = 'cover',
    avatarReady = true,
    avatarUnavailable = false,
    avatarDegraded = false,
    onContinueDegraded,
    queuePosition = null,
    queueEtaSeconds = null,
    queueDepth = null,
    language = 'de',
  }, ref) => {
    const preparingText = language === 'de' ? 'Interviewer wird vorbereitet...' : 'Preparing interviewer...'
    const unavailableTitle = language === 'de'
      ? 'Aktuell kein Interviewer verfügbar'
      : 'No interviewer available right now'
    const unavailableBody = language === 'de'
      ? 'Alle KI-Interviewer sind gerade beschäftigt. Bitte lade die Seite in einer Minute neu.'
      : 'All AI interviewers are currently busy. Please refresh this page in a minute.'
    const refreshLabel = language === 'de' ? 'Erneut versuchen' : 'Try Again'

    const degradedTitle = language === 'de'
      ? 'Avatar nicht verfügbar'
      : 'Avatar not available'
    const degradedBody = language === 'de'
      ? 'Du kannst das Interview ohne Avatar fortsetzen.'
      : 'You can continue the interview without an avatar.'
    const continueLabel = language === 'de'
      ? 'Ohne Avatar fortfahren'
      : 'Continue without avatar'

    const queuedTitle = language === 'de'
      ? 'Du wartest in der Warteschlange'
      : "You're in line"
    const queuedPositionLabel = (pos: number, depth: number | null) => {
      const human = pos + 1
      if (language === 'de') {
        return depth != null
          ? `Du bist #${human} von ${depth} in der Warteschlange.`
          : `Du bist #${human} in der Warteschlange.`
      }
      return depth != null
        ? `You are #${human} of ${depth} waiting.`
        : `You are #${human} in line.`
    }
    const queuedEtaLabel = (sec: number) => language === 'de'
      ? `Geschätzte Wartezeit: ${formatEtaText(sec, language)}`
      : `Estimated wait: ${formatEtaText(sec, language)}`
    const queuedBody = language === 'de'
      ? 'Wir starten dein Interview, sobald ein Interviewer verfügbar ist.'
      : "We'll start your interview as soon as an interviewer becomes available."

    // Priority order when multiple overlays could apply:
    //   unavailable > degraded > queued > preparing > none
    const showUnavailableOverlay = avatarEnabled && avatarUnavailable
    const showDegradedOverlay =
      avatarEnabled && !showUnavailableOverlay && avatarDegraded
    const showQueuedOverlay =
      avatarEnabled && !showUnavailableOverlay && !showDegradedOverlay && queuePosition !== null && queuePosition >= 0
    const showPreparingOverlay =
      avatarEnabled && !isConnecting && !avatarReady && !showUnavailableOverlay && !showDegradedOverlay && !showQueuedOverlay

    return (
      <>
        {avatarEnabled && (
          <video
            ref={ref}
            autoPlay
            playsInline
            muted={false}
            id="avatar-video"
            style={{
              width: '100%',
              height: '100%',
              objectFit: fitMode,
              objectPosition: fitMode === 'contain' ? 'center center' : 'center top',
              display: 'block',
            }}
          />
        )}
        {(!avatarEnabled || isConnecting) && (
          <div id="video-placeholder" className="video-placeholder">
            <div className="avatar-placeholder">
              <div className="avatar-circle">
                <img className="avatar-icon" src="/images/logo.png" alt="AI Avatar" />
              </div>
              <div className="avatar-name" id="avatar-name">{t(language, 'aiInterviewer')}</div>
              {isConnecting && (
                <>
                  <p className="waiting-cozy-text">{t(language, 'waitingCozy')}</p>
                  <div className="waiting-dots" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {showPreparingOverlay && (
          <div
            className="iv-avatar-loading"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 16,
              background: 'rgba(10, 12, 20, 0.65)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              color: 'rgba(255,255,255,0.92)',
              fontFamily: "'DM Sans', 'Roboto', sans-serif",
              fontSize: 14,
              letterSpacing: '0.5px',
              pointerEvents: 'none',
              zIndex: 5,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                border: '3px solid rgba(255,255,255,0.2)',
                borderTopColor: 'rgba(255,255,255,0.92)',
                borderRadius: '50%',
                animation: 'iv-avatar-spin 0.9s linear infinite',
              }}
            />
            <div>{preparingText}</div>
            <style>{`@keyframes iv-avatar-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
        {showQueuedOverlay && (
          <div
            className="iv-avatar-queued"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 14,
              padding: '0 32px',
              background: 'rgba(10, 12, 20, 0.78)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              color: 'rgba(255,255,255,0.95)',
              fontFamily: "'DM Sans', 'Roboto', sans-serif",
              textAlign: 'center',
              pointerEvents: 'none',
              zIndex: 5,
            }}
          >
            <div style={{ fontSize: 36 }} aria-hidden>⏳</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{queuedTitle}</div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)' }}>
              {queuedPositionLabel(queuePosition as number, queueDepth ?? null)}
            </div>
            {queueEtaSeconds !== null && queueEtaSeconds !== undefined && queueEtaSeconds > 0 && (
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
                {queuedEtaLabel(queueEtaSeconds)}
              </div>
            )}
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 4, maxWidth: 360 }}>
              {queuedBody}
            </div>
          </div>
        )}
        {showUnavailableOverlay && (
          <div
            className="iv-avatar-unavailable"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              padding: '0 32px',
              background: 'rgba(10, 12, 20, 0.85)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              color: 'rgba(255,255,255,0.95)',
              fontFamily: "'DM Sans', 'Roboto', sans-serif",
              textAlign: 'center',
              zIndex: 6,
            }}
          >
            <div style={{ fontSize: 36 }} aria-hidden>⚠️</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{unavailableTitle}</div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', maxWidth: 380 }}>
              {unavailableBody}
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: 8,
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: 500,
                color: 'rgba(255,255,255,0.95)',
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 8,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {refreshLabel}
            </button>
          </div>
        )}
        {showDegradedOverlay && (
          <div
            className="iv-avatar-degraded"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              padding: '0 32px',
              background: 'rgba(10, 12, 20, 0.85)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              color: 'rgba(255,255,255,0.95)',
              fontFamily: "'DM Sans', 'Roboto', sans-serif",
              textAlign: 'center',
              zIndex: 6,
            }}
          >
            <div style={{ fontSize: 36 }} aria-hidden>⚠️</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{degradedTitle}</div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', maxWidth: 380 }}>
              {degradedBody}
            </div>
            <button
              onClick={() => onContinueDegraded?.()}
              style={{
                marginTop: 8,
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: 500,
                color: 'rgba(255,255,255,0.95)',
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 8,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {continueLabel}
            </button>
          </div>
        )}
      </>
    )
  }
)

AvatarVideo.displayName = 'AvatarVideo'
